import type { DomainGuide } from '@/components/guide/guide-types';

import th from './th.json';
import en from './en.json';
import zh from './zh.json';
import vi from './vi.json';
import km from './km.json';
import lo from './lo.json';
import my from './my.json';

/**
 * Help catalog for the `tickets` domain.
 *
 * Read out of `apps/admin/src/routes/tickets.ts`, `repo/tickets.ts` and
 * `repo/ticket-state.ts`, not out of the spec. The facts that decide what an
 * operator can trust on these two screens:
 *
 *   - the list is the WHOLE AGENT's queue, not the reader's own tickets — every
 *     staff account of the brand sees every ticket of the brand, and none of
 *     another brand's
 *   - the list and the menu badge poll on the SAME ten-second clock
 *     (`TICKET_POLL_MS`), and a hidden tab stops polling entirely
 *   - only `deposit` and `withdraw` demand an image, and the server refuses
 *     those without one (TICKET-003) whatever the button says
 *   - an evidence image is looked at through a link that expires after five
 *     minutes; nothing on either screen ever holds the stored object
 *   - the ticket code's date is the BANGKOK day, not the reader's
 *   - nothing on the LIST closes a ticket: taking one on, finishing it and
 *     refusing it live on the detail page the ticket code opens, and the person
 *     who opened a ticket is never the one who accepts it
 *   - a finished ticket carries a result meant to be relayed word for word; a
 *     refused one carries a reason and no result, and the two are different
 *     fields
 *   - the trace is append-only, and its "from where" column is filled in only
 *     for staff holding the approve permission
 */
export const ticketsGuide: DomainGuide = {
  domain: 'tickets',
  pages: [
    {
      id: 'tickets.list',
      route: '/tickets',
      permission: 'ticket:read',
      status: 'live',
      fields: [
        { key: 'code', kind: 'text' },
        { key: 'status', kind: 'status' },
        { key: 'kind', kind: 'text' },
        { key: 'priority', kind: 'status' },
        { key: 'member', kind: 'text' },
        { key: 'subject', kind: 'text' },
        { key: 'evidence', kind: 'text' },
        { key: 'openedBy', kind: 'text' },
        { key: 'openedAt', kind: 'date' },
      ],
      cautionCount: 6,
      warningCount: 0,
    },
    {
      /**
       * Where a ticket is actually worked: taken on, finished, refused, and
       * read back afterwards by whoever opened it.
       */
      id: 'tickets.detail',
      route: '/tickets/:id',
      // `ticket:read` opens the page; the four review buttons on it need
      // `ticket:approve`, which the gateway resolves on its own resource.
      permission: 'ticket:read',
      status: 'live',
      fields: [
        { key: 'status', kind: 'status' },
        { key: 'member', kind: 'text' },
        { key: 'linkedRecord', kind: 'text' },
        { key: 'evidence', kind: 'text' },
        { key: 'result', kind: 'text' },
        { key: 'rejectReason', kind: 'text' },
        { key: 'remark', kind: 'text' },
        { key: 'trace', kind: 'text' },
      ],
      cautionCount: 5,
      warningCount: 0,
    },
    {
      id: 'tickets.new',
      route: '/tickets/new',
      permission: 'ticket:create',
      status: 'live',
      fields: [
        { key: 'username', kind: 'text' },
        { key: 'kind', kind: 'text' },
        { key: 'priority', kind: 'status' },
        { key: 'subject', kind: 'text' },
        { key: 'detail', kind: 'text' },
        { key: 'linkedRecord', kind: 'text' },
        { key: 'evidence', kind: 'text' },
      ],
      cautionCount: 5,
      warningCount: 0,
    },
  ],
  copy: { th, en, zh, vi, km, lo, my },
};
